(function () {
  'use strict';

  const CONTESTED_GOOGLE = ['_gclid', '_gbraid', '_wbraid', '_click_timestamp'];
  const CONTESTED_KEYS = CONTESTED_GOOGLE.concat([
    '_msclkid',
    '_fbclid',
    '_ttclid',
    '_epik',
    '_source',
    '_medium',
  ]);

  const AttributionToCart = {
    // Configuration
    config: {
      attributePrefix: '_',
      maxRetries: 3,
      retryDelay: 1000,
    },

    // Check if cart already has attribution
    hasAttribution: function () {
      return sessionStorage.getItem('attribution_saved_to_cart') === 'true';
    },

    // Mark attribution as saved
    markAttributionSaved: function () {
      try {
        sessionStorage.setItem('attribution_saved_to_cart', 'true');
      } catch (_e) {
        // Silent fail
      }
    },

    // Get attribution from sessionStorage
    getAttribution: function () {
      try {
        const data = sessionStorage.getItem('attribution');
        if (!data) return null;

        const attribution = JSON.parse(data);

        // Validate required fields
        if (!attribution.source) {
          return null;
        }

        return attribution;
      } catch (_e) {
        return null;
      }
    },

    // The canonical record in localStorage, or the session copy converted to its shape when the canonical one is missing.
    getCanonicalRecord: function (attribution) {
      try {
        const stored = JSON.parse(localStorage.getItem('coracrea_attribution') || 'null');
        if (stored && typeof stored === 'object') return stored;
      } catch (_e) {
        // Fall through to the session copy
      }
      const rule = window.CoraAttributionRule;
      return rule ? rule.sessionToRecord(attribution) : {};
    },

    // Convert attribution to cart attributes format
    buildCartAttributes: function (attribution) {
      const prefix = this.config.attributePrefix;
      const attributes = {};

      // Map attribution fields to cart attributes
      if (attribution.source) {
        attributes[prefix + 'source'] = attribution.source;
      }
      if (attribution.medium) {
        attributes[prefix + 'medium'] = attribution.medium;
      }
      if (attribution.campaign) attributes[prefix + 'campaign'] = attribution.campaign;
      if (attribution.content) attributes[prefix + 'content'] = attribution.content;
      if (attribution.term) attributes[prefix + 'term'] = attribution.term;
      if (attribution.landingPage) attributes[prefix + 'landing_page'] = attribution.landingPage;
      if (attribution.referrer) attributes[prefix + 'referrer'] = attribution.referrer;
      // `_timestamp` is written only by attribution-tracker.js (ISO, one per visit), never here. `_click_timestamp` below is click-scoped, and the offline-conversion staleness guard (extractClickTimestamp in scripts/upload-offline-conversions.js) reads it in preference to `_timestamp`.
      if (attribution.click_timestamp) {
        attributes[prefix + 'click_timestamp'] = String(attribution.click_timestamp);
      }

      // Add tracking IDs if present
      if (attribution.gclid) attributes[prefix + 'gclid'] = attribution.gclid;
      if (attribution.fbclid) attributes[prefix + 'fbclid'] = attribution.fbclid;
      if (attribution.ttclid) attributes[prefix + 'ttclid'] = attribution.ttclid;
      if (attribution.msclkid) attributes[prefix + 'msclkid'] = attribution.msclkid;
      if (attribution.epik) attributes[prefix + 'epik'] = attribution.epik;

      // Add blog attribution if present
      if (attribution.blog_source) attributes[prefix + 'blog_source'] = attribution.blog_source;
      if (attribution.blog_handle) attributes[prefix + 'blog_handle'] = attribution.blog_handle;
      if (attribution.blog_article) attributes[prefix + 'blog_article'] = attribution.blog_article;
      if (attribution.blog_title) attributes[prefix + 'blog_title'] = attribution.blog_title;
      if (attribution.blog_url) attributes[prefix + 'blog_url'] = attribution.blog_url;
      if (attribution.blog_timestamp) {
        attributes[prefix + 'blog_timestamp'] = String(attribution.blog_timestamp);
      }
      if (attribution.blog_visited) {
        attributes[prefix + 'blog_visited'] = String(attribution.blog_visited);
      }

      // Contested keys come from the canonical record, so this writer repeats what early capture decided; the Google tuple is taken whole and a blank value is never posted.
      const canonical = this.getCanonicalRecord(attribution);
      const hasGoogle = CONTESTED_GOOGLE.some((key) => canonical[key]);
      CONTESTED_KEYS.forEach((key) => {
        const attributeName = prefix + key.slice(1);
        if (CONTESTED_GOOGLE.indexOf(key) !== -1 && !hasGoogle) return;
        if (CONTESTED_GOOGLE.indexOf(key) !== -1) delete attributes[attributeName];
        if (canonical[key]) attributes[attributeName] = String(canonical[key]);
      });

      return attributes;
    },

    // Write through the shared serialized writer, which skips keys the cart already holds
    updateCart: function (attributes) {
      this.postCart(attributes, 0);
    },

    // Update cart via Cart API
    postCart: function (attributes, retryCount) {
      const self = this;

      window.CoraCartAttributes.write(attributes)
        .then(function (result) {
          if (!result.ok) {
            throw new Error('Cart API returned status ' + result.status);
          }
          if (result.skipped) {
            self.markAttributionSaved();
            return null;
          }
          return result.cart || {};
        })
        .then(function (cart) {
          if (!cart) return;
          self.markAttributionSaved();

          // Verify attributes were saved
          if (cart.attributes && cart.attributes[self.config.attributePrefix + 'source']) {
            // Send success event to GA4 for tracking
            if (typeof window.gtag === 'function') {
              window.gtag('event', 'attribution_saved_to_cart', {
                event_category: 'Attribution Tracking',
                source: cart.attributes[self.config.attributePrefix + 'source'],
                medium: cart.attributes[self.config.attributePrefix + 'medium'],
                campaign: cart.attributes[self.config.attributePrefix + 'campaign'],
                non_interaction: true,
              });
            }
          }
        })
        .catch(function (error) {
          // Retry with exponential backoff
          if (retryCount < self.config.maxRetries) {
            const delay = self.config.retryDelay * Math.pow(2, retryCount);

            setTimeout(function () {
              self.postCart(attributes, retryCount + 1);
            }, delay);
          } else {
            // Send error event to GA4
            if (typeof window.gtag === 'function') {
              window.gtag('event', 'attribution_save_failed', {
                event_category: 'Attribution Tracking',
                error_message: error.message,
                retry_count: retryCount,
                non_interaction: true,
              });
            }
          }
        });
    },

    // Main initialization
    init: function () {
      // Check if attribution already saved to cart
      if (this.hasAttribution()) {
        return;
      }

      // Get attribution from sessionStorage
      const attribution = this.getAttribution();
      if (!attribution) {
        return;
      }

      // Build cart attributes
      const cartAttributes = this.buildCartAttributes(attribution);
      if (Object.keys(cartAttributes).length === 0) {
        return;
      }

      // Wait for page to be fully loaded before updating cart
      const self = this;
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
          self.updateCart(cartAttributes);
        });
      } else {
        // DOM already loaded
        self.updateCart(cartAttributes);
      }
    },
  };

  // Auto-initialize
  AttributionToCart.init();

  // Make available globally for debugging
  window.AttributionToCart = AttributionToCart;
})();
